import { useRef } from 'react'

type AutoFocusHandler = (event: Event) => void

// Chrome honours this option; TypeScript's DOM types do not list it yet.
const WITHOUT_RING = { focusVisible: false } as FocusOptions

let returningFocus = false

/** True only while focus is being handed back, so a tooltip on the opener can stay shut. */
export const isQuietFocusReturn = () => returningFocus

const asElement = (target: EventTarget | null) => (target instanceof HTMLElement ? target : null)

/** A dropdown has no open event, but its content stays labelled by its trigger. */
const openerAtClose = (content: HTMLElement | null) => {
	const triggerId = content?.getAttribute('aria-labelledby')
	return triggerId ? document.getElementById(triggerId) : null
}

/**
 * While open, a trigger points at its content with aria-controls. A dialog opened without a trigger
 * falls back to whatever had focus, which a click on a button leaves unset in Safari. A menu item
 * unmounts as its menu closes, so the menu's trigger stands in for it.
 */
const openerAtOpen = (content: HTMLElement | null) => {
	// Radix ids hold no quotes or backslashes, so the attribute value needs no escaping.
	const trigger = content?.id ? document.querySelector<HTMLElement>(`[aria-controls="${content.id}"]`) : null
	const active = asElement(document.activeElement)
	const menuTrigger = openerAtClose(active?.closest<HTMLElement>('[role="menu"]') ?? null)
	return trigger ?? menuTrigger ?? (active === document.body ? null : active)
}

/**
 * Radix hands focus back to whatever opened an overlay when it closes. After a keyboard close Chrome
 * then paints that element's focus ring and opens its tooltip, which reads as a stray highlight. Focus
 * still goes back, so keyboard users keep their place; it just lands without the ring or the tooltip.
 */
export function useQuietFocusReturn(onOpenAutoFocus?: AutoFocusHandler, onCloseAutoFocus?: AutoFocusHandler) {
	const opener = useRef<HTMLElement | null>(null)
	return {
		onOpenAutoFocus: (event: Event) => {
			opener.current = openerAtOpen(asElement(event.target))
			onOpenAutoFocus?.(event)
		},
		onCloseAutoFocus: (event: Event) => {
			onCloseAutoFocus?.(event)
			if (event.defaultPrevented) {
				return
			}
			event.preventDefault()
			const content = asElement(event.target)
			const active = document.activeElement
			// A click outside that landed on something focusable keeps its focus.
			if (active && active !== document.body && !content?.contains(active)) {
				return
			}
			returningFocus = true
			try {
				;(opener.current ?? openerAtClose(content))?.focus(WITHOUT_RING)
			} finally {
				returningFocus = false
			}
		},
	}
}
